import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useGetAllCoursesUserSideQuery } from "../../services/course";

import CourseCard from "../cards/CourseCard";

const getDisplayText = (value, fallback = "") => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return fallback;
  if (typeof value.categoryName === "string") return value.categoryName;
  if (typeof value.title === "string") return value.title;
  return fallback;
};

const getCourseCategoryLabels = (courseCategory) => {
  if (!courseCategory) return [];

  if (Array.isArray(courseCategory)) {
    return courseCategory.map((item) => getDisplayText(item)).filter(Boolean);
  }

  const label = getDisplayText(courseCategory);
  return label ? [label] : [];
};

const getCourseCategoryOptions = (courseCategory) => {
  if (!courseCategory) return [];

  const toOption = (item) => {
    if (!item?._id) return null;
    const label = getDisplayText(item);
    return label ? { id: item._id, label } : null;
  };

  if (Array.isArray(courseCategory)) {
    return courseCategory.map(toOption).filter(Boolean);
  }

  const option = toOption(courseCategory);
  return option ? [option] : [];
};

const OurCourses = ({ title, showSearch = false, padding }) => {
  const [activeCategoryId, setActiveCategoryId] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  const { data: categoriesSource } = useGetAllCoursesUserSideQuery({
    page: 1,
  });

  const courseCategories = useMemo(() => {
    const categories = new Map();

    (categoriesSource?.data || []).forEach((course) => {
      getCourseCategoryOptions(course?.courseCategory).forEach(({ id, label }) => {
        categories.set(id, label);
      });
    });

    return Array.from(categories.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [categoriesSource?.data]);

  const courseQueryParams = useMemo(
    () => ({
      page: 1,
      ...(activeCategoryId !== "All" ? { category: activeCategoryId } : {}),
      ...(showSearch && debouncedSearchTerm.trim()
        ? { search: debouncedSearchTerm.trim() }
        : {}),
    }),
    [activeCategoryId, debouncedSearchTerm, showSearch],
  );

  const {
    data: coursesResponse,
    error,
    isLoading,
    isError,
  } = useGetAllCoursesUserSideQuery(courseQueryParams);
  const isNoCoursesError =
    isError &&
    error?.status === 404 &&
    getDisplayText(error?.data?.message) === "No courses found";

  const courses = Array.isArray(coursesResponse?.data)
    ? coursesResponse.data.map((course) => ({
        id: course._id || "",
        img: course.courseThumbnail?.secureUrl || "",
        name: getDisplayText(course.courseTitle, "Untitled Course"),
        desc:
          course.courseOverview?.courseDescription ||
          "No description available.",
        age: getDisplayText(course.courseAgeGroup, "All ages"),
        duration: course.courseOverview?.courseDuration || "Self paced",
        level: getDisplayText(course.courseLevel, "—"),
        activities: getCourseCategoryLabels(course.courseCategory)[0] || "",
      }))
    : [];

  const hasNoResults = !isError && courses.length === 0;

  return (
    <div className="bg-white px-4 sm:px-6 lg:px-8">
      <div className={`mx-auto max-w-[1920px] ${padding}`}>
        <h1 className="text-center text-[26px] font-bold leading-tight text-black sm:text-[32px] sm:leading-[38px]">
          {title}
        </h1>
        <p className="mx-auto mt-3 max-w-3xl text-center text-sm font-normal leading-5 text-black sm:mt-[14px]">
          The world is changing fast — it’s not just about learning anymore.
          It’s about staying relevant. Choose the course of your liking and
          start building what’s next.
        </p>
        {showSearch && (
          <div className="mt-6 flex justify-center sm:mt-8">
            <div className="relative w-full max-w-[892px]">
              <input
                type="text"
                placeholder="Search Course"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-11 w-full border border-[#333333] bg-white pl-4 text-sm text-black placeholder:text-black focus:outline-none sm:h-[46px] sm:text-base"
              />
              <Search
                size={16}
                strokeWidth={2.2}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black"
              />
            </div>
          </div>
        )}
        <div className="mt-6 -mx-4 px-4 sm:mx-0 sm:mt-10 sm:px-0 2xl:mt-[50px]">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none sm:flex-wrap sm:justify-center sm:gap-4 sm:overflow-visible sm:pb-0 lg:gap-8 2xl:gap-12 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {courseCategories.length > 0 ? (
              <button
                type="button"
                onClick={() => setActiveCategoryId("All")}
                className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium leading-5 transition-colors sm:px-4 sm:py-1 sm:text-lg lg:px-[23px] lg:py-[5px] lg:text-xl lg:leading-[23px] ${
                  activeCategoryId === "All"
                    ? "bg-[#FF6636] text-white"
                    : "bg-transparent text-black hover:bg-[#FF66361A]"
                }`}
              >
                All
              </button>
            ) : null}
            {courseCategories.map((category) => {
              const isActive = category.id === activeCategoryId;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategoryId(category.id)}
                  className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium leading-5 transition-colors sm:px-4 sm:py-1 sm:text-lg lg:px-[23px] lg:py-[5px] lg:text-xl lg:leading-[23px] ${
                    isActive
                      ? "bg-[#FF6636] text-white"
                      : "bg-transparent text-black hover:bg-[#FF66361A]"
                  }`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>
        <div
          className={
            showSearch
              ? "pb-9 sm:pb-14 2xl:pb-[72px]"
              : "pb-14 sm:pb-20 2xl:pb-[106px]"
          }
        >
          {isLoading ? (
            <div className="mt-10 flex justify-center">
              <div className="flex min-h-[220px] w-full max-w-2xl items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-6 text-center">
                <p className="text-lg font-medium text-gray-700">
                  Loading courses...
                </p>
              </div>
            </div>
          ) : isNoCoursesError || hasNoResults ? (
            <div className="mt-10 flex justify-center">
              <div className="flex min-h-[220px] w-full max-w-2xl flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900">
                  No courses found
                </h3>
                <p className="mt-2 text-gray-600">
                  We couldn't find any courses matching your search. Try a
                  different keyword or category.
                </p>
              </div>
            </div>
          ) : isError ? (
            <div className="mt-10 flex justify-center">
              <div className="flex min-h-[220px] w-full max-w-2xl flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 px-6 text-center">
                <h3 className="text-xl font-semibold text-red-700">
                  Something went wrong
                </h3>
                <p className="mt-2 text-red-600">
                  We couldn't load the courses right now. Please refresh the
                  page or try again later.
                </p>
              </div>
            </div>
          ) : (
            <div className="mx-auto mt-8 grid max-w-[1724px] grid-cols-1 gap-x-4 gap-y-6 min-[480px]:grid-cols-2 sm:mt-10 sm:gap-x-6 sm:gap-y-8 lg:mt-14 lg:grid-cols-3 lg:gap-y-10 xl:gap-y-16 2xl:mt-[72px] 2xl:gap-y-24">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OurCourses;
